import {
  useState, useEffect, useCallback, useRef,
} from "react";
import { useLocation } from "react-router";

type InfiniteScrollOptions = {
  loading: boolean
  hasMore: boolean
  error?: unknown
  callback: () => void
  threshold?: number
  rootMargin?: string
  resetScrollOnMount?: boolean
};

type InfiniteScrollReturn<T> = {
  loadMoreRef: (node: T | null) => void
  isLoadingMore: boolean
};

export const useInfiniteScroll = <T extends HTMLElement>({
  loading,
  hasMore,
  error,
  callback,
  threshold = 0.2,
  rootMargin = "800px",
  resetScrollOnMount = true,
}: InfiniteScrollOptions): InfiniteScrollReturn<T> => {
  const elementRef = useRef<T | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const location = useLocation();
  const isLoadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const callbackRef = useRef(callback);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // ----- UPDATE CALLBACK REF WHEN CALLBACK CHANGES ----- //
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // ----- RESET SCROLL POSITION ON MOUNT AND ROUTE CHANGE ----- //
  useEffect(() => {
    if (resetScrollOnMount) window.scrollTo(0, 0);
  }, [resetScrollOnMount, location.pathname]);

  // ----- SETUP OBSERVER FUNCTION ----- //
  const setupObserver = useCallback(() => {
    // CLEAR DEBOUNCE TIMER IF EXISTS
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // DISCONNECT PREVIOUS OBSERVER IF EXISTS
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // CREATE NEW OBSERVER IF CONDITIONS ARE MET
    const element = elementRef.current;
    if (element && hasMore && !loading && !error) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (entry.isIntersecting && !isLoadingRef.current && hasMore) {
            // CLEAR PREVIOUS DEBOUNCE TIMER
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
            }

            // DEBOUNCE THE CALLBACK TO PREVENT MULTIPLE CALLS
            debounceRef.current = setTimeout(() => {
              // CHECK CONDITIONS AGAIN TO PREVENT STALE CALLS
              if (!isLoadingRef.current && hasMore) {
                isLoadingRef.current = true;
                setIsLoadingMore(true);
                callbackRef.current();
              }
            }, 100);
          }
        },
        {
          threshold,
          rootMargin,
        },
      );
      observerRef.current.observe(element);
    }
  }, [hasMore, loading, error, threshold, rootMargin]);

  // ----- CALLBACK REF TO SET ELEMENT WHEN MOUNTED ----- //
  const loadMoreRef = useCallback(
    (node: T | null) => {
      elementRef.current = node;
      // Trigger observer setup manually when element changes
      setupObserver();
    },
    [setupObserver],
  );

  // ----- SETUP OBSERVER WHEN CONDITIONS CHANGE ----- //
  useEffect(() => {
    setupObserver();
  }, [setupObserver]);

  // ----- WHEN FETCH COMPLETES, RESET FLAGS ----- //
  useEffect(() => {
    if (!loading) {
      isLoadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [loading]);

  // ----- CLEANUP OBSERVER AND DEBOUNCE ON UNMOUNT ----- //
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    loadMoreRef,
    isLoadingMore,
  };
};
